import React from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import safetyImg from '../assets/Homepage/safty.png';
import supportiveDriversImg from '../assets/Homepage/supportive drivers.png';

export default function ValueProps({ cards, safety, theme = {} }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sectionBg = theme.sectionBg || '#fff';
  const altBg = theme.sectionAltBg || '#f6f6f6';
  const cardBg = theme.cardBg || '#fff';
  const cardAltBg = theme.cardAltBg || '#f6f6f6';
  const text = theme.text || '#000';
  const muted = theme.muted || '#545454';
  const link = theme.link || text;
  const border = theme.border || 'rgba(15,23,42,0.10)';

  const defaultFeatures = [
    {
      image: supportiveDriversImg,
      title: t('ride_with_plukk') || 'Ride with Plukk',
      desc: t('ride_with_plukk_desc') || 'Request a ride and get picked up by a nearby driver in minutes.',
      link: '/login',
      linkText: t('get_a_ride') || 'Get a ride',
    },
    {
      image: safetyImg,
      title: t('drive_with_plukk') || 'Drive with Plukk',
      desc: t('drive_with_plukk_desc') || 'Set your own hours and earn money on your schedule.',
      link: '/login',
      linkText: t('become_a_driver') || 'Become a driver',
    },
  ];

  const features = (cards && cards.length ? cards : defaultFeatures).map((feature, idx) => ({
    ...feature,
    image: feature.image || (idx === 0 ? supportiveDriversImg : safetyImg),
    desc: feature.desc || feature.description,
  }));

  const safetyContent = safety || {};
  const safetyTitle = safetyContent.title || t('safety_first') || 'Safety is a top priority';
  const safetyDesc =
    safetyContent.description ||
    t('safety_desc') ||
    "Whether you're in the back seat or behind the wheel, your safety is essential. We are committed to doing our part, and technology is at the heart of our approach.";
  const safetyLink = safetyContent.link || '/about-us';
  const safetyLinkText = safetyContent.linkText || t('learn_about_safety') || 'Learn about safety';
  const safetyFeatures = safetyContent.features && safetyContent.features.length
    ? safetyContent.features
    : [
        { title: t('driver_screening') || 'Driver screening', desc: t('driver_screening_desc') || 'Background checks before driving' },
        { title: t('real_time_support') || 'Real-time support', desc: t('real_time_support_desc') || '24/7 customer service' },
        { title: t('trip_tracking') || 'Trip tracking', desc: t('trip_tracking_desc') || 'Share your ride status with family' },
      ];

  return (
    <Box sx={{ bgcolor: sectionBg, py: { xs: 6, md: 10 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 } }}>
        <Grid container spacing={4}>
          {features.map((feature, idx) => (
            <Grid key={idx} item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: cardAltBg,
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: `1px solid ${border}`,
                }}
              >
                <Box
                  sx={{
                    height: 280,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: 24, md: 28 },
                      color: text,
                      mb: 2,
                      fontFamily: '"UberMove", -apple-system, sans-serif',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: muted,
                      fontSize: 16,
                      lineHeight: 1.6,
                      mb: 3,
                      flex: 1,
                    }}
                  >
                    {feature.desc}
                  </Typography>
                  <Button
                    onClick={() => navigate(feature.link)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      alignSelf: 'flex-start',
                      color: link,
                      fontWeight: 500,
                      fontSize: 16,
                      textTransform: 'none',
                      p: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {feature.linkText}
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 8,
            bgcolor: altBg,
            borderRadius: 3,
            p: { xs: 4, md: 6 },
            border: `1px solid ${border}`,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 28, md: 36 },
                  color: text,
                  mb: 2,
                  fontFamily: '"UberMove", -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                {safetyTitle}
              </Typography>
              <Typography
                sx={{
                  color: muted,
                  fontSize: 16,
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                {safetyDesc}
              </Typography>
              <Button
                onClick={() => navigate(safetyLink)}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  color: link,
                  fontWeight: 500,
                  fontSize: 16,
                  textTransform: 'none',
                  p: 0,
                  '&:hover': {
                    bgcolor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {safetyLinkText}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {safetyFeatures.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      bgcolor: cardBg,
                      borderRadius: 2,
                      p: 3,
                      flex: '1 1 200px',
                      border: `1px solid ${border}`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 16, color: text, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: muted, fontSize: 14 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
